import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import {
  IPayloadOperationMonitoring,
  IResponseMonitoringDetail,
  IResponseOperationMonitoring,
} from '../../interfaces/monitoring';
import { ApiService } from '../../services/api.service';
import { DOWNLOAD_FILE } from '../config/config.actions';
import {
  DOWNLOAD_MONITORING_REPORT,
  DOWNLOAD_MONITORING_REPORT_SAP,
  DOWNLOAD_MONITORING_REPORT_DASHBOARD,
  RETRIVE_DETAIL_MONITORING_OPERATION,
  RETRIVE_DETAIL_MONITORING_OPERATION_SUCCESS,
  RETRIVE_MONITORING_OPERATION,
  RETRIVE_MONITORING_OPERATION_SUCCESS,
} from './monitoring.actions';
import { PersistenceService } from '../../services/persistence.service';

@Injectable()
export class MonitoringEffects {
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private persistence: PersistenceService
  ) {}

  retriveMonitoringOperation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RETRIVE_MONITORING_OPERATION),
      switchMap(
        ({
          payload,
          sigla,
        }: {
          payload: IPayloadOperationMonitoring;
          sigla: string;
        }) => {
          return forkJoin({
            payload: of(payload),
            operationMonitoring: this.api.monitoringOperation(payload, sigla),
          });
        }
      ),
      map(
        ({
          payload,
          operationMonitoring,
        }: {
          payload: IPayloadOperationMonitoring;
          operationMonitoring: IResponseOperationMonitoring;
        }) => {
          this.persistence.setMonitoringFilters(payload.operazione);
          this.persistence.setMonitoringList({ result: operationMonitoring });

          return RETRIVE_MONITORING_OPERATION_SUCCESS({ operationMonitoring });
        }
      ),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  retriveDetailMonitoringOperation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RETRIVE_DETAIL_MONITORING_OPERATION),
      switchMap(({ idOperazione }: { idOperazione: number }) => {
        return this.api.retriveMonitoringDetail(idOperazione);
      }),
      map((operationMonitoringDetail: IResponseMonitoringDetail) => {
        return RETRIVE_DETAIL_MONITORING_OPERATION_SUCCESS({
          operationMonitoringDetail,
        });
      }),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  downloadMonitoringReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DOWNLOAD_MONITORING_REPORT),
      switchMap(
        ({
          operazioni,
          rete,
          tab,
          fileName,
        }: {
          operazioni: any;
          rete: string;
          tab: string;
          fileName?: string;
        }) => {
          return this.api
            .downloadMonitoringReport(operazioni, rete, tab)
            .pipe(map((response: Blob) => ({ response, fileName, rete })));
        }
      ),
      map(
        ({
          response,
          fileName,
          rete,
        }: {
          response: any;
          fileName?: string;
          rete: string;
        }) => {
          // Nome del file predefinito nel caso in cui fileName non sia fornito

          const now = new Date();
          const nowSuffix =
            now.getFullYear().toString().padStart(4, '0') +
            (now.getMonth() + 1).toString().padStart(2, '0') +
            now.getDate().toString().padStart(2, '0') +
            '_' +
            now.getHours().toString().padStart(2, '0') +
            now.getMinutes().toString().padStart(2, '0') +
            now.getSeconds().toString().padStart(2, '0');

          return DOWNLOAD_FILE({
            file: response.body,
            fileName: `${fileName}${nowSuffix}.xlsx`,
          });
        }
      ),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  downloadMonitoringReportSap$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DOWNLOAD_MONITORING_REPORT_SAP),
      switchMap(
        ({
          operazioni,
          rete,
          tab,
          fileName,
        }: {
          operazioni: any;
          rete: string;
          tab: string;
          fileName?: string;
        }) => {
          return forkJoin({
            rete: of(rete),
            fileName: of(fileName),
            response: this.api.downloadMonitoringReportSap(
              operazioni,
              rete,
              tab
            ),
          });
        }
      ),
      map(
        ({
          rete,
          response,
          fileName,
        }: {
          rete: string;
          response: any;
          fileName?: string;
        }) => {
          // Nome del file predefinito nel caso in cui fileName non sia fornito
          const defaultFileName = `${rete}_operazioni_monitoraggio_interfacce_sap_`;

          const now = new Date();
          const nowSuffix =
            now.getFullYear().toString().padStart(4, '0') +
            (now.getMonth() + 1).toString().padStart(2, '0') +
            now.getDate().toString().padStart(2, '0') +
            '_' +
            now.getHours().toString().padStart(2, '0') +
            now.getMinutes().toString().padStart(2, '0') +
            now.getSeconds().toString().padStart(2, '0');

          return DOWNLOAD_FILE({
            file: response.body,
            fileName: `${fileName || defaultFileName}${nowSuffix}.xlsx`,
          });
        }
      ),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });

  downloadMonitoringReportDashboard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DOWNLOAD_MONITORING_REPORT_DASHBOARD),
      switchMap(
        ({
          operazioni,
          rete,
          tab,
          fileName,
        }: {
          operazioni: any;
          rete: string;
          tab: string;
          fileName?: string;
        }) => {
          return forkJoin({
            rete: of(rete),
            fileName: of(fileName),
            response: this.api.downloadMonitoringReportDashboard(
              operazioni,
              rete,
              tab
            ),
          });
        }
      ),
      map(
        ({
          rete,
          response,
          fileName,
        }: {
          rete: string;
          response: any;
          fileName?: string;
        }) => {
          // Nome del file predefinito nel caso in cui fileName non sia fornito
          const defaultFileName = `${rete}_operazioni_monitoraggio_interfacce_dashboard_`;

          const now = new Date();
          const nowSuffix =
            now.getFullYear().toString().padStart(4, '0') +
            (now.getMonth() + 1).toString().padStart(2, '0') +
            now.getDate().toString().padStart(2, '0') +
            '_' +
            now.getHours().toString().padStart(2, '0') +
            now.getMinutes().toString().padStart(2, '0') +
            now.getSeconds().toString().padStart(2, '0');

          return DOWNLOAD_FILE({
            file: response.body,
            fileName: `${fileName || defaultFileName}${nowSuffix}.xlsx`,
          });
        }
      ),
      catchError((error: HttpErrorResponse) => {
        return throwError({ error });
      })
    );
  });
}
